import {
	ConditionListFieldItemDataObject,
	ConditionListItemDataObject,
} from 'Core/components/advanced/ConditionList/ListItem';
import {
	CONDITION_LIST_FIELD_ITEM_DISPLAY_TYPE,
	CONDITION_LIST_FIELD_ITEM_EMPTY_VALUES
} from 'Core/components/advanced/ConditionList/const';
import {getNumber, getString, resetObjectValues} from 'Core/helpers/data';
import {cloneDeep, get} from 'lodash';
import * as typeDataMap from './type';
import * as comparatorDataMap from './comparator';
import * as logicalOperatorDataMap from './logicalOperator';
import {getDate, getDateString, STANDARD_DATE_TIME_FORMAT} from 'Core/helpers/datetime';
import {ltrimString} from 'Core/helpers/string';
import {translate} from 'Core/i18n';
import {COMPARATOR} from 'Core/const/global';
import {isTag} from 'Tags/helpers';

/**
 * Get input data from raw data
 * @param {Object} rawData - Raw data received from IO.
 * @return {ConditionListItemDataObject}
 */
export const input = rawData => {
	const displayType = typeDataMap.input(getString(rawData, 'fieldType'), getString(rawData, 'operand1'));
	const comparator = comparatorDataMap.input(getString(rawData, 'comparisonOperator'));
	let value = get(rawData, 'operand2', CONDITION_LIST_FIELD_ITEM_EMPTY_VALUES[
		typeDataMap.input(getString(rawData, 'fieldType'), getString(rawData, 'operand1'))
	]);
	if ((
			displayType === CONDITION_LIST_FIELD_ITEM_DISPLAY_TYPE.DATE ||
			displayType === CONDITION_LIST_FIELD_ITEM_DISPLAY_TYPE.DATETIME
		) && !!value
	) {
		if (!isTag(value)) value = getDate(value, STANDARD_DATE_TIME_FORMAT.ISO_DATE_TIME_S);
	}
	
	return new ConditionListItemDataObject(
		logicalOperatorDataMap.input(getString(rawData, 'logicalOperator')),
		new ConditionListFieldItemDataObject(
			(
				getString(rawData, 'operand1').startsWith('Custom Field:') ?
					ltrimString(getString(rawData, 'operand1'), 'Custom Field:') :
					translate(getString(rawData, 'operand1'), 'constants.customerFields')
			),
			getString(rawData, 'operand1'),
			undefined,
			displayType
		),
		comparator,
		value,
		getNumber(rawData, 'openParenthesis'),
		getNumber(rawData, 'closeParenthesis'),
		undefined,
		getNumber(rawData, 'ordinal'),
		rawData
	);
};

/**
 * Get output data from input data
 * @param {ConditionListItemDataObject} inputData - Input data.
 * @return {Object}
 */
export const output = inputData => {
	let operand2 = inputData.value;
	if ((
			inputData.field.displayType === CONDITION_LIST_FIELD_ITEM_DISPLAY_TYPE.DATE ||
			inputData.field.displayType === CONDITION_LIST_FIELD_ITEM_DISPLAY_TYPE.DATETIME
		) && !!operand2
	) {
		if (!isTag(operand2)) operand2 = getDateString(operand2, STANDARD_DATE_TIME_FORMAT.ISO_DATE_TIME_S);
	} else if (inputData.comparator === COMPARATOR.BTW) {
		// BTW (between) comparator support operand2 to be an array of two values so we need to join them into a string 
		// for IO using a comma (,) as a separator because IO request cannot have an array as a value. If operand2 is not 
		// an array, it should be passed as-is.
		operand2 = (Array.isArray(operand2) ? operand2.join(',') : operand2);
	}
	
	let result = cloneDeep({
		operand1: inputData.field.value,
		fieldType: typeDataMap.output(inputData.field.displayType),
		operand2,
		logicalOperator: logicalOperatorDataMap.output(inputData.logicalOperator),
		comparisonOperator: comparatorDataMap.output(inputData.comparator),
		openParenthesis: inputData.openParenthesis,
		closeParenthesis: inputData.closeParenthesis,
		ordinal: inputData.ordinal
	});

	// Set proper empty values expected by the API
	result = resetObjectValues(result, {
		operand2: {
			...CONDITION_LIST_FIELD_ITEM_EMPTY_VALUES,
			// Empty value for dates is set to empty string because API treats dates as string values
			// @note This is not done in 'input' function since Date object is needed and null is a valid Date object.
			[CONDITION_LIST_FIELD_ITEM_DISPLAY_TYPE.DATE]: '',
			[CONDITION_LIST_FIELD_ITEM_DISPLAY_TYPE.DATETIME]: '',
		}[inputData.field.displayType],
		logicalOperator: null,
	});
	
	return result;
};